import { createReducer, on } from '@ngrx/store';

import { INITIAL_LOCATION_UPLOAD_STATE } from '@ps/survey-utils';

import { INITIAL_SURVEY_CREATION_AUDIENCE_STATE } from '../survey-creation-state.constants';
import { SurveyCreationAudienceState } from '../survey-creation-state.model';
import {
  SurveyCreationActions,
  SurveyCreationApiActions,
} from './survey-creation-audience.actions';

export const surveyCreationAudienceReducer = createReducer(
  INITIAL_SURVEY_CREATION_AUDIENCE_STATE,

  on(
    SurveyCreationActions.init,
    (state, { surveyId }): SurveyCreationAudienceState => {
      // Already hydrated for this exact survey (e.g. just transitioned from
      // create → edit) — keep the in-memory survey instead of wiping it and
      // forcing loadSurveyForEdit$ to re-fetch what we already have.
      const alreadyHydrated =
        !!surveyId && state.surveyId === surveyId && !!state.survey;

      return alreadyHydrated
        ? state
        : { ...INITIAL_SURVEY_CREATION_AUDIENCE_STATE, surveyId };
    },
  ),

  on(
    SurveyCreationApiActions.loadSurveyForEditSuccess,
    (state, { survey }): SurveyCreationAudienceState => ({
      ...state,
      survey,
      error: null,
    }),
  ),

  on(
    SurveyCreationActions.updateMetadata,
    (state, { metadata }): SurveyCreationAudienceState => ({
      ...state,
      metadata,
    }),
  ),

  on(
    SurveyCreationActions.updateQualifications,
    (state, { qualifications }): SurveyCreationAudienceState => ({
      ...state,
      qualifications,
    }),
  ),

  on(
    SurveyCreationActions.updateQuotas,
    (state, { quotas }): SurveyCreationAudienceState => ({
      ...state,
      quotas,
    }),
  ),

  on(
    SurveyCreationActions.uploadLocationCodes,
    (state, { qualificationCode }): SurveyCreationAudienceState => ({
      ...state,
      locationFileUpload: {
        qualificationCode,
        loading: true,
        error: null,
        filePath: null,
        result: null,
      },
    }),
  ),

  on(
    SurveyCreationApiActions.uploadLocationCodesSuccess,
    (state, { qualificationCode, result }): SurveyCreationAudienceState => ({
      ...state,
      locationFileUpload: {
        qualificationCode,
        loading: false,
        error: null,
        filePath: result.filePath,
        result,
      },
    }),
  ),

  on(
    SurveyCreationApiActions.uploadLocationCodesFailure,
    (state, { qualificationCode, error }): SurveyCreationAudienceState => ({
      ...state,
      locationFileUpload: {
        qualificationCode,
        loading: false,
        error,
        filePath: null,
        result: null,
      },
    }),
  ),

  on(
    SurveyCreationActions.clearLocationCodes,
    (state, { qualificationCode }): SurveyCreationAudienceState =>
      state.locationFileUpload.qualificationCode === qualificationCode
        ? { ...state, locationFileUpload: { ...INITIAL_LOCATION_UPLOAD_STATE } }
        : state,
  ),

  on(
    SurveyCreationApiActions.createStandardSurveySuccess,
    (state, { survey }): SurveyCreationAudienceState => ({
      ...state,
      surveyId: survey.id,
      survey,
      error: null,
    }),
  ),

  on(
    SurveyCreationApiActions.createStandardSurveyFailure,
    (state, { error }): SurveyCreationAudienceState => ({
      ...state,
      error,
    }),
  ),

  on(
    SurveyCreationApiActions.saveSurveySuccess,
    (state, { survey }): SurveyCreationAudienceState => ({
      ...state,
      surveyId: survey.id,
      survey,
      error: null,
    }),
  ),

  on(
    SurveyCreationApiActions.saveSurveyFailure,
    (state, { error }): SurveyCreationAudienceState => ({
      ...state,
      error,
    }),
  ),

  on(
    SurveyCreationApiActions.loadSurveyForEditFailure,
    (state, { error }): SurveyCreationAudienceState => ({
      ...state,
      error,
    }),
  ),

  on(
    SurveyCreationActions.reset,
    (): SurveyCreationAudienceState => INITIAL_SURVEY_CREATION_AUDIENCE_STATE,
  ),
);
